import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { IUserEntity } from '@boma/client-shared';
import { ILocation, IVehicle } from '@boma/supplier-portal/shared/entities';
import { LocationsService } from '@boma/supplier-portal/shared/services';
import { getUser } from '@boma/supplier-portal/shared/state';
import { LocationType } from '@boma/website/shared/entities';
import { Store } from '@ngrx/store';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Table } from 'primeng/table';
import { Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'boma-location-search',
  templateUrl: './boma-location-search.component.html',
  styleUrls: ['./boma-location-search.component.scss'],
})

/**
 * TODO Implement a dropdown with virtual scrolling
 */
export class BomaLocationSearchComponent implements OnInit {
  @ViewChild('op') op: OverlayPanel;
  @ViewChild('dt1') table: Table;
  @ViewChild('inputField') inputField: ElementRef;

  isScrolledToBottom: boolean;

  @Input() type: LocationType = null;
  @Input() multipleLocations = true;
  @Input() showLabel = false;
  @Input() label = 'Location';
  @Input() placeHolderText = 'All Locations';
  @Output() locationSelectedChange = new EventEmitter();

  user$: Observable<IUserEntity>;
  selectedOrganizationId?: string;
  locations: ILocation | IVehicle | any = [];
  filteredLocations: ILocation | IVehicle | any = [];
  selectedLocations: any[];

  originalOverflow: string;

  currentPage = 1;
  perPage = 50;
  query = '';
  searching = false;

  firstRow = 0;

  loadedAll = false;

  paginationData: {
    totalItems: number;
    totalPages: number;
    previousPage: number;
    nextPage: number;
  };

  totalExistingLocations: number;

  locationsLoaded = false;
  loadingNextPage = false;

  selectedTypes = [];
  locationTypeOptions = [
    {
      name: 'Locations',
      value: 'Physical',
    },
    {
      name: 'Vehicles',
      value: 'Vehicle',
    },
  ];

  filterValue: string = '';

  constructor(
    private locationService: LocationsService,
    private store: Store
  ) {
    this.user$ = this.store.select(getUser);
    this.user$.subscribe((user) => {
      this.selectedOrganizationId = user.selectedOrganizationId;
    });
    this.selectedLocations = [];

    this.paginationData = {
      totalItems: null,
      totalPages: null,
      previousPage: null,
      nextPage: null,
    };
  }

  private searchLocations$ = new Subject<string>();

  ngOnInit(): void {
    this.searchLocations$
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.searching = true;
        }),
        switchMap((query) =>
          this.locationService.searchLocations(
            this.selectedOrganizationId,
            this.query,
            this.type,
            this.currentPage,
            this.perPage
          )
        )
      )
      .subscribe((locations) => {
        if (this.query == '' && !this.locationsLoaded) {
          this.totalExistingLocations =
            this.paginationData.totalItems < locations.totalItems
              ? locations.totalItems
              : this.paginationData.totalItems;
        }

        this.paginationData = {
          totalItems: locations.totalItems,
          totalPages: locations.totalPages,
          previousPage: locations.previousPage,
          nextPage: locations.nextPage,
        };

        this.locationsLoaded = true;
        this.searching = false;
        this.loadingNextPage = false;

        this.updateLocationResults(locations);
      });
  }

  focusFilterField() {
    setTimeout(() => {
      this.inputField.nativeElement.focus();
    }, 0);
  }

  applyFilter() {
    this.table?.filterGlobal(this.filterValue, 'contains');
  }

  sortByName(array) {
    array.sort((a, b) => a.name.localeCompare(b.name));
  }

  searchLocations() {
    this.searching = true;
    const distinctQuery = this.query + this.currentPage;
    this.searchLocations$.next(distinctQuery);
  }

  loadMore() {
    this.currentPage++;
    const distinctQuery = this.query + this.currentPage;
    this.searchLocations$.next(distinctQuery);
  }

  firstLoad() {
    if (!this.locationsLoaded) {
      this.searchLocations();
    }
  }

  toggleOverlay(event) {
    if (this.op) {
      this.op.show(event);
      this.focusFilterField();
    }
    this.firstLoad();
  }

  onFilterLocations(event) {
    this.query = event;
    this.searchLocations$.next(this.query);
  }

  onRowSelect(event) {
    if (!this.multipleLocations) {
      this.selectedLocations = [event.data];
      this.op.hide();
    }

    this.emitLocations();
  }

  onRowUnselect(event) {
    if (!this.multipleLocations) this.selectedLocations = [];
    this.emitLocations();
  }

  emitLocations() {
    this.locationSelectedChange.emit(this.selectedLocations);
  }

  clear() {
    this.selectedLocations = [];
    this.filterValue = '';
    this.applyFilter();
    this.emitLocations();
  }

  onTableScroll(event: any) {
    if (this.loadedAll) return;

    const element = event.target as HTMLElement;
    const atBottom =
      element.scrollHeight - (element.scrollTop + element.clientHeight) < 105; // allowing for some margin of error also preload the results before i get to them

    if (atBottom && !this.loadingNextPage) {
      this.loadingNextPage = true;
      this.loadMore();
    }
  }

  disableScroll(): void {
    this.originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.focusFilterField();
  }

  enableScroll(): void {
    document.body.style.overflow = this.originalOverflow;
  }

  // Sometimes thing go wrong... so we need to just help people
  loadAllLocations() {
    this.locationService
      .searchLocations(
        this.selectedOrganizationId,
        '',
        this.type,
        1,
        this.totalExistingLocations
      )
      .subscribe((x) => {
        this.updateLocationResults(x);
      });
  }

  updateLocationResults(locations) {
    const newLocations: ILocation[] = Object.values(locations.items);

    const uniqueItems = newLocations.filter((newItem) => {
      return !this.locations.some((existingItem) => {
        return existingItem.id === newItem?.id;
      });
    });

    this.locations = [...this.locations, ...uniqueItems];
    this.filteredLocations = newLocations;

    this.sortByName(this.locations);

    if (this.locations.length === this.totalExistingLocations)
      this.loadedAll = true;

    this.focusFilterField();
  }
}
